import * as tus from "tus-js-client";
import {
  UploadRequest,
  UploadAcknowledge,
  UploadHeartbeat,
  UploadResign,
  UploadCancel,
  getProgressStatus,
} from "../api/HttpApis";

/**
 * Upload manager — the single owner of every in-flight video upload.
 * ============================================================================
 *
 * This is a module-level singleton on purpose. React components subscribe to it;
 * they never own an upload. Closing the media modal, navigating to another page or
 * unmounting the popup therefore cannot interrupt a 30GB transfer, and progress is
 * still there when the user comes back.
 *
 * Why it exists (the bugs it replaces):
 *
 *  1. A flat 1-hour presigned signature meant Bunny started answering 401 mid-upload
 *     on any large file, throwing away every byte already sent. The manager keeps a
 *     rolling signature (see scheduleResign / onShouldRetry) so an upload can run for
 *     as long as it needs.
 *  2. Upload progress lived only in React state, so a reload erased it and the media
 *     grid fell back to a hardcoded "Processing 50%". The manager reports byte offsets
 *     to the server (upload-heartbeat), making progress durable and shared.
 *  3. Resume was disabled outright, so any hiccup restarted the whole file. The manager
 *     resumes via the exact stored upload URL for that videoId, which is safe — the old
 *     "stuck at 0 bytes" bug came from tus's own fingerprint storage reattaching bytes
 *     to a *stale* video; we never use that (storeFingerprintForResuming: false).
 *  4. The encode poll gave up after ~12 minutes and then reported success, so long
 *     transcodes looked finished when they weren't. Polling here backs off and only
 *     stops on a real terminal state.
 *
 * Phases an item moves through:
 *   uploading -> processing -> ready
 *   uploading -> paused -> uploading
 *   uploading -> interrupted (recoverable, needs the file again after a reload)
 *   uploading|processing -> failed
 */

const STORAGE_KEY = "ff_upload_queue_v1";

const HEARTBEAT_MS = 5000;            // how often byte progress is reported to the server
const RESIGN_CHECK_MS = 60_000;       // how often we check the signature deadline
const RESIGN_MARGIN_MS = 15 * 60_000; // re-sign once this little validity is left

// Encode polling. Cadence is driven by ELAPSED TIME, not poll count: a small file can
// finish transcoding in under a minute, so the first couple of minutes must be responsive,
// while a multi-hour 4K transcode should poll cheaply.
//
// The previous version backed off by poll count AND only refreshed from Bunny every 3rd
// poll, which left a 70-second gap between real readings — the CMS showed "Transcoding 5%"
// while Bunny was already finishing. Every poll now refreshes; the backend's own ~15s
// Bunny status cache is what protects Bunny from being hammered.
// The Bunny webhook is now the primary signal for "encoding finished", so polling exists to
// animate the bar and as a fallback — it does not need to be aggressive. These intervals were
// deliberately relaxed after a 6.7GB upload filled the network tab: a 30-minute transcode
// went from ~100 polls to ~35.
//
// Polling faster would not help accuracy anyway: Bunny's *API* reports encodeProgress well
// behind their own dashboard (observed 15% via API while the dashboard showed 65%), so the
// limiting factor is Bunny, not our cadence.
const ENCODE_FIRST_POLL_MS = 4_000;
const ENCODE_POLL_STEPS = [
  { untilMs: 60_000, everyMs: 10_000 },      // first minute: catch short transcodes
  { untilMs: 10 * 60_000, everyMs: 30_000 },
];
const ENCODE_POLL_SLOW_MS = 60_000;          // beyond 10 min
const ENCODE_POLL_CEILING_MS = 6 * 60 * 60 * 1000; // 6h, then stop polling but keep the card

/** Poll interval for how long this transcode has been running. */
function encodePollDelay(elapsedMs) {
  const step = ENCODE_POLL_STEPS.find((s) => elapsedMs < s.untilMs);
  return step ? step.everyMs : ENCODE_POLL_SLOW_MS;
}

// tus retry schedule. Long tail so a transient network drop on a multi-hour upload
// is ridden out rather than failing the whole transfer.
const RETRY_DELAYS = [0, 1000, 3000, 5000, 10_000, 20_000, 30_000, 60_000, 60_000];

/**
 * Chunk size scaled to the file so the request count (and progress granularity) stays
 * sane across a 20MB clip and a 30GB feature. Bounds matter:
 *  - too small: thousands of round trips, throughput dies on a high-latency link
 *  - too large: a single retry re-sends a lot, and progress updates become chunky
 *
 * NOTE: we deliberately do NOT set tus's `parallelUploads`. That option splits the file
 * into separate uploads stitched together with the TUS *concatenation* extension, which
 * Bunny's tusupload endpoint does not advertise support for. Setting it would break
 * uploads rather than speed them up.
 */
// Measured against real Bunny with a 60MB file and production retry settings:
//   5MB chunks  -> 13 requests -> 5.38 MB/s
//   20MB chunks ->  5 requests -> 7.63 MB/s
//   48MB chunks ->  3 requests -> 7.44 MB/s
// Per-request round trips dominate, so the old 5MB floor made every file under 2GB about
// 30-40% slower than it needed to be. Large files were already fine (a 30GB file lands on
// the 48MB cap either way). 16MB floor keeps retry cost bounded — a dropped chunk re-sends
// at most 48MB — while cutting request count several-fold.
//
// Bunny accepts chunk sizes that are NOT multiples of 256 KiB (verified), so the arbitrary
// ceil(size/400) values in the middle band are safe.
const MIN_CHUNK = 16 * 1024 * 1024;  // 16MB
const MAX_CHUNK = 48 * 1024 * 1024;  // 48MB
const TARGET_CHUNKS = 400;
export function chunkSizeFor(fileSize) {
  const size = Number(fileSize) || 0;
  if (size <= 0) return MIN_CHUNK;
  // Files smaller than one chunk go in a single request, which is the fastest case.
  return Math.min(MAX_CHUNK, Math.max(MIN_CHUNK, Math.ceil(size / TARGET_CHUNKS)));
}

/**
 * Requests already stamped with auth headers. WeakSet so finished requests are collected.
 *
 * The guard lives here, with the function it protects, rather than at the call site — that
 * way NO caller can corrupt a request, however the code is reorganised later.
 */
const stampedRequests = new WeakSet();

/**
 * Stamp Bunny's auth headers onto a tus request. Idempotent per request object.
 *
 * In a browser, `xhr.setRequestHeader(name, value)` APPENDS when the name is already set
 * ("old, new") rather than replacing. Setting these headers from two places therefore put
 * `LibraryId: "598077, 598077"` on the wire and Bunny rejected every upload with
 * 400 "Library ID missing or invalid." Node's http stack overwrites instead, so this was
 * invisible outside a real browser.
 */
export function applyAuthHeaders(req, headers) {
  if (!req || !headers) return;
  if (stampedRequests.has(req)) return;
  stampedRequests.add(req);
  Object.entries(headers).forEach(([k, v]) => {
    if (v !== undefined && v !== null) req.setHeader(k, String(v));
  });
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

/** videoId -> public item state (what the UI renders) */
const items = new Map();
/** videoId -> runtime handles that must never be serialised (File, tus.Upload, timers) */
const runtime = new Map();

const listeners = new Set();
let refreshGrid = null; // set by the UI so a finished upload can refresh the media list

// tus fires onProgress many times a second. Notifying React on every one of them would
// re-render the whole media grid dozens of times a second, and writing localStorage that
// often would block the main thread — on a big upload that shows up as a janky CMS.
// Progress updates are therefore coalesced; state *transitions* still publish at once.
const EMIT_THROTTLE_MS = 250;
const PERSIST_THROTTLE_MS = 2000;

let emitTimer = null;
let emitPending = false;
let persistTimer = null;

function publish() {
  const snapshot = Array.from(items.values());
  listeners.forEach((fn) => {
    try {
      fn(snapshot);
    } catch (err) {
      console.error("upload listener failed", err);
    }
  });
}

/** Coalesced notify — use for high-frequency updates such as byte progress. */
function emit() {
  if (emitTimer) {
    emitPending = true;
  } else {
    publish();
    emitTimer = setTimeout(() => {
      emitTimer = null;
      if (emitPending) {
        emitPending = false;
        emit();
      }
    }, EMIT_THROTTLE_MS);
  }

  if (!persistTimer) {
    persistTimer = setTimeout(() => {
      persistTimer = null;
      persist();
    }, PERSIST_THROTTLE_MS);
  }
}

/** Immediate notify + save — use when an item changes phase or leaves the queue. */
function emitNow() {
  if (emitTimer) {
    clearTimeout(emitTimer);
    emitTimer = null;
  }
  emitPending = false;
  if (persistTimer) {
    clearTimeout(persistTimer);
    persistTimer = null;
  }
  publish();
  persist();
}

export function subscribe(fn) {
  listeners.add(fn);
  fn(Array.from(items.values()));
  return () => listeners.delete(fn);
}

export function getItems() {
  return Array.from(items.values());
}

/** Let the media library register a refresh callback so finished uploads appear. */
export function setGridRefresh(fn) {
  refreshGrid = fn;
}

function notifyGrid() {
  if (typeof refreshGrid === "function") {
    try {
      refreshGrid();
    } catch (err) {
      console.error("grid refresh failed", err);
    }
  }
}

function patch(videoId, changes) {
  const current = items.get(videoId);
  if (!current) return;
  const next = { ...current, ...changes };
  items.set(videoId, next);
  // A phase change is what the user is waiting to see, so never delay it.
  if (changes.phase && changes.phase !== current.phase) emitNow();
  else emit();
}

// ---------------------------------------------------------------------------
// Persistence
// ---------------------------------------------------------------------------
// Only metadata is stored. A File handle cannot survive a reload, so an upload that
// was mid-flight comes back as `interrupted` + `needsFile`, and the user re-picks the
// same file to resume from the byte offset the server already knows about. Items in
// `processing` need no file at all — polling simply restarts.

const PERSISTED_PHASES = ["uploading", "paused", "processing", "interrupted", "failed"];

function persist() {
  try {
    const serialisable = Array.from(items.values())
      .filter((it) => PERSISTED_PHASES.includes(it.phase))
      .map((it) => ({
        videoId: it.videoId,
        videoGuid: it.videoGuid,
        title: it.title,
        fileName: it.fileName,
        fileSize: it.fileSize,
        // A paused/uploading item can only be resumed by hand after a reload.
        phase: it.phase === "uploading" || it.phase === "paused" ? "interrupted" : it.phase,
        progress: it.progress,
        encodeProgress: it.encodeProgress,
        bytesUploaded: it.bytesUploaded,
        error: it.error,
      }));
    if (serialisable.length === 0) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(serialisable));
    }
  } catch {
    // Storage full or unavailable (private mode). Losing the durability of the *UI*
    // list is survivable — the server still has the real progress.
  }
}

let restored = false;

/**
 * Rebuild the queue after a page load. Safe to call more than once.
 * Uploads come back as recoverable rather than silently vanishing, which is the whole
 * point: the user must be able to see that a 30GB transfer was interrupted.
 */
export function restore() {
  if (restored) return;
  restored = true;
  let saved = [];
  try {
    saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
  } catch {
    saved = [];
  }
  if (!Array.isArray(saved) || saved.length === 0) return;

  saved.forEach((it) => {
    if (!it?.videoId || items.has(it.videoId)) return;
    const phase = it.phase === "processing" ? "processing" : it.phase === "failed" ? "failed" : "interrupted";
    items.set(it.videoId, {
      ...blankItem(it),
      phase,
      progress: phase === "interrupted" ? Number(it.progress) || 0 : Number(it.progress) || 0,
      encodeProgress: Number(it.encodeProgress) || 0,
      bytesUploaded: Number(it.bytesUploaded) || 0,
      error: it.error || null,
      // Only a byte-transfer needs the file back; a transcode does not.
      needsFile: phase === "interrupted",
    });
    if (phase === "processing") trackEncoding(it.videoId);
  });
  emitNow();
}

function blankItem(seed) {
  return {
    videoId: seed.videoId,
    videoGuid: seed.videoGuid || null,
    title: seed.title || seed.fileName || "Untitled",
    fileName: seed.fileName || seed.title || "",
    fileSize: Number(seed.fileSize) || 0,
    phase: "uploading",
    progress: 0,
    encodeProgress: 0,
    bytesUploaded: 0,
    bytesPerSec: 0,
    etaSeconds: null,
    error: null,
    needsFile: false,
  };
}

// ---------------------------------------------------------------------------
// Speed / ETA
// ---------------------------------------------------------------------------
// An exponentially-weighted rate, so the readout is steady enough to be believed but
// still reacts when the connection changes. A raw instantaneous rate jitters so much
// that users read it as "broken".

function updateRate(videoId, bytesUploaded, total) {
  const rt = runtime.get(videoId);
  if (!rt) return { bytesPerSec: 0, etaSeconds: null };
  const now = Date.now();

  if (rt.lastSampleAt == null) {
    rt.lastSampleAt = now;
    rt.lastSampleBytes = bytesUploaded;
    return { bytesPerSec: rt.rate || 0, etaSeconds: null };
  }

  const dt = (now - rt.lastSampleAt) / 1000;
  if (dt < 1) {
    // Too soon to say anything meaningful; reuse the smoothed value.
    const remaining = Math.max(0, total - bytesUploaded);
    return {
      bytesPerSec: rt.rate || 0,
      etaSeconds: rt.rate > 0 ? Math.round(remaining / rt.rate) : null,
    };
  }

  const instant = Math.max(0, (bytesUploaded - rt.lastSampleBytes) / dt);
  rt.rate = rt.rate ? rt.rate * 0.7 + instant * 0.3 : instant;
  rt.lastSampleAt = now;
  rt.lastSampleBytes = bytesUploaded;

  const remaining = Math.max(0, total - bytesUploaded);
  return {
    bytesPerSec: rt.rate,
    etaSeconds: rt.rate > 0 ? Math.round(remaining / rt.rate) : null,
  };
}

// ---------------------------------------------------------------------------
// Server heartbeat
// ---------------------------------------------------------------------------

function sendHeartbeat(videoId, bytesUploaded, force = false) {
  const rt = runtime.get(videoId);
  if (!rt) return;
  const now = Date.now();
  if (!force && now - (rt.lastHeartbeatAt || 0) < HEARTBEAT_MS) return;
  rt.lastHeartbeatAt = now;

  UploadHeartbeat(videoId, {
    bytesUploaded,
    ...(rt.uploadUrl ? { tusUploadUrl: rt.uploadUrl } : {}),
  }).catch(() => {
    // Deliberately silent. A missed heartbeat only costs a little server-side
    // freshness; failing the upload over it would be far worse.
  });
}

// ---------------------------------------------------------------------------
// Rolling signature
// ---------------------------------------------------------------------------

/** Fetch a fresh signature for an upload already in progress and swap it in. */
async function resign(videoId) {
  const rt = runtime.get(videoId);
  if (!rt) return null;
  try {
    const res = await UploadResign(videoId, { contentType: rt.contentType });
    const data = res?.data?.data;
    if (!data?.headers) return null;
    rt.authHeaders = buildAuthHeaders(data.headers);
    rt.expiresAtMs = data.expiresAt ? Number(data.expiresAt) * 1000 : null;
    return rt.authHeaders;
  } catch (err) {
    console.warn(`Could not refresh upload signature for video ${videoId}`, err);
    return null;
  }
}

function scheduleResign(videoId) {
  const rt = runtime.get(videoId);
  if (!rt) return;
  clearInterval(rt.resignTimer);
  rt.resignTimer = setInterval(() => {
    const cur = runtime.get(videoId);
    if (!cur) return;
    if (!cur.expiresAtMs) return;
    if (cur.expiresAtMs - Date.now() > RESIGN_MARGIN_MS) return;
    resign(videoId);
  }, RESIGN_CHECK_MS);
}

function buildAuthHeaders(headers) {
  return {
    AuthorizationSignature: headers.AuthorizationSignature,
    AuthorizationExpire: String(headers.AuthorizationExpire),
    LibraryId: String(headers.LibraryId),
    ...(headers.VideoId ? { VideoId: String(headers.VideoId) } : {}),
  };
}

// ---------------------------------------------------------------------------
// The upload itself
// ---------------------------------------------------------------------------

/**
 * Begin (or resume) pushing bytes to Bunny for an item that already exists in the queue.
 * @param {number} videoId
 * @param {File} file
 * @param {{ endpoint: string, authHeaders: object, uploadUrl?: string|null, metadata?: object }} cfg
 */
function startTus(videoId, file, cfg) {
  const rt = runtime.get(videoId);
  if (!rt) return;

  rt.file = file;
  rt.authHeaders = cfg.authHeaders;
  rt.uploadUrl = cfg.uploadUrl || rt.uploadUrl || null;
  rt.contentType = file.type || "video/mp4";

  const upload = new tus.Upload(file, {
    endpoint: cfg.endpoint,
    // Resuming an existing session: hand tus the exact URL that belongs to THIS
    // videoId. Bunny is HEADed for the current offset and the transfer picks up
    // there instead of restarting the file.
    ...(rt.uploadUrl ? { uploadUrl: rt.uploadUrl } : {}),
    chunkSize: chunkSizeFor(file.size),
    retryDelays: RETRY_DELAYS,
    // We manage resumption ourselves via the stored uploadUrl. tus's own fingerprint
    // storage is what previously reattached bytes to a stale video ("0 Bytes / stuck
    // Uploading"), so it stays off.
    storeFingerprintForResuming: false,
    removeFingerprintOnSuccess: true,

    // NOTE: the Bunny auth headers are deliberately NOT passed via `headers` here.
    // They are set ONLY in onBeforeRequest below — see the warning there. Setting them
    // in both places produced `LibraryId: "598077, 598077"` on the wire and Bunny
    // rejected every upload with 400 "Library ID missing or invalid.".
    metadata: {
      filetype: cfg.metadata?.filetype || file.type || "video/mp4",
      title: cfg.metadata?.title || file.name,
    },

    // Every request gets the *current* signature, not the one captured when the
    // upload started. This is what lets a multi-hour transfer outlive its original
    // 1-hour token without a restart.
    //
    // ⚠️  This is the ONLY place the auth headers may be set.
    // tus's browser stack does `xhr.setRequestHeader(name, value)` immediately on every
    // setHeader call, and per the XHR spec a repeated header name is *appended* as
    // "old, new" rather than replaced. (Its internal `_headers` map does overwrite, so
    // debug output looks correct while the wire is corrupt — that cost real debugging
    // time.) Node's http stack overwrites, which is why this only broke in the browser.
    //
    // Async on purpose: if a re-sign is in flight (see onShouldRetry) we wait for it
    // here, so the retry goes out with the NEW signature. Without this the first
    // retry has a 0ms delay and would just 401 again with the stale header.
    onBeforeRequest: async (req) => {
      const cur = runtime.get(videoId);
      if (!cur) return;
      if (cur.resignPromise) {
        try {
          await cur.resignPromise;
        } catch {
          /* fall through with whatever headers we have */
        }
        cur.resignPromise = null;
      }
      const headers = runtime.get(videoId)?.authHeaders;
      if (!headers) return;

      // applyAuthHeaders is idempotent per request object, so even if tus were to invoke
      // this twice for one request the headers still cannot be appended/corrupted.
      applyAuthHeaders(req, headers);
    },

    // A 401 means the signature aged out (or was rotated). Kick off a re-sign and let
    // the request be retried — onBeforeRequest blocks on it. The old code surfaced this
    // as a dead end and discarded the whole transfer.
    onShouldRetry: (error, retryAttempt) => {
      const status = error?.originalResponse?.getStatus?.();
      const cur = runtime.get(videoId);
      if (status === 401 || status === 403) {
        if (cur && !cur.resignPromise) cur.resignPromise = resign(videoId);
        return retryAttempt < RETRY_DELAYS.length;
      }
      if (status && status >= 400 && status < 500 && status !== 409 && status !== 423) {
        return false; // genuine client error, retrying won't help
      }
      return retryAttempt < RETRY_DELAYS.length;
    },

    onUploadUrlAvailable: () => {
      const cur = runtime.get(videoId);
      if (!cur) return;
      cur.uploadUrl = upload.url;
      // Persist the URL server-side immediately: it's the only thing that makes this
      // upload resumable from another session.
      sendHeartbeat(videoId, cur.lastBytes || 0, true);
    },

    onProgress: (bytesUploaded, bytesTotal) => {
      const cur = runtime.get(videoId);
      if (!cur) return;
      cur.lastBytes = bytesUploaded;
      const total = bytesTotal || file.size || 0;
      const progress = total > 0 ? Math.min(100, Math.floor((bytesUploaded / total) * 100)) : 0;
      const { bytesPerSec, etaSeconds } = updateRate(videoId, bytesUploaded, total);
      patch(videoId, {
        phase: "uploading",
        progress,
        bytesUploaded,
        bytesPerSec,
        etaSeconds,
        error: null,
        needsFile: false,
      });
      sendHeartbeat(videoId, bytesUploaded);
    },

    onSuccess: () => {
      const cur = runtime.get(videoId);
      if (cur) {
        clearInterval(cur.resignTimer);
        cur.resignTimer = null;
      }
      patch(videoId, {
        phase: "processing",
        progress: 100,
        bytesUploaded: file.size,
        bytesPerSec: 0,
        etaSeconds: null,
        error: null,
      });
      sendHeartbeat(videoId, file.size, true);

      // Acknowledge, then follow the transcode. Both paths poll: a failed ack must not
      // hide a video that Bunny is happily encoding.
      UploadAcknowledge({ videoId })
        .catch((err) => console.warn(`Acknowledge failed for video ${videoId}`, err))
        .finally(() => {
          notifyGrid();
          trackEncoding(videoId);
        });
    },

    onError: (error) => {
      const cur = runtime.get(videoId);
      if (cur?.aborted) return; // pause/cancel, not a failure

      // Log everything Bunny told us. A bare status code is not enough to diagnose a
      // rejected upload — Bunny puts the actual reason in the response body.
      const res = error?.originalResponse;
      const status = res?.getStatus?.();
      const body = (() => {
        try {
          return res?.getBody?.();
        } catch {
          return undefined;
        }
      })();
      console.error(`TUS upload failed for video ${videoId}`, {
        status,
        body,
        message: error?.message,
        uploadUrl: cur?.uploadUrl || upload.url || null,
        // Which request failed matters: no uploadUrl yet ⇒ it was session creation.
        stage: cur?.uploadUrl || upload.url ? "transfer (PATCH/HEAD)" : "session creation (POST)",
        sentHeaders: cur?.authHeaders ? Object.keys(cur.authHeaders) : null,
        error,
      });

      // Surface Bunny's own words — a naked "(400)" gives the user nothing to act on.
      const detail = typeof body === "string" && body.trim() ? ` — ${body.trim().slice(0, 200)}` : "";

      // The bytes already at Bunny are still there, and the row still exists, so this
      // is recoverable — never present it as "start over".
      patch(videoId, {
        phase: "interrupted",
        bytesPerSec: 0,
        etaSeconds: null,
        needsFile: false,
        error:
          status === 401 || status === 403
            ? "Upload authorisation expired. Click Resume to continue where it left off."
            : `Upload interrupted${status ? ` (${status})` : ""}${detail}. Click Resume to continue.`,
      });
    },
  });

  rt.upload = upload;
  rt.aborted = false;
  upload.start();
  scheduleResign(videoId);
}

// ---------------------------------------------------------------------------
// Public actions
// ---------------------------------------------------------------------------

/**
 * Add a file to the queue and start uploading it.
 * The card is created *before* any bytes move, so the user sees the upload appear
 * instantly rather than watching their selection vanish for several minutes.
 * @returns {Promise<{ videoId: number }>}
 */
export async function enqueue(file, { title } = {}) {
  const res = await UploadRequest({
    fileName: file.name,
    fileSize: file.size,
    contentType: file.type,
    title: title || file.name,
  });

  const info = res?.data?.data;
  const videoId = info?.videoId;
  if (!info?.endpoint || !info?.headers || !videoId) {
    throw new Error("Invalid upload request response");
  }

  items.set(videoId, {
    ...blankItem({
      videoId,
      videoGuid: info.videoGuid,
      title: title || file.name,
      fileName: file.name,
      fileSize: file.size,
    }),
  });

  runtime.set(videoId, {
    file,
    endpoint: info.endpoint,
    authHeaders: buildAuthHeaders(info.headers),
    expiresAtMs: info.expiresAt ? Number(info.expiresAt) * 1000 : null,
    metadata: info.metadata,
    contentType: file.type || "video/mp4",
    uploadUrl: null,
    lastBytes: 0,
    rate: 0,
  });

  emitNow();
  // Make the placeholder row visible in the grid straight away.
  notifyGrid();

  startTus(videoId, file, {
    endpoint: info.endpoint,
    authHeaders: buildAuthHeaders(info.headers),
    metadata: info.metadata,
  });

  return { videoId };
}

/**
 * Register a video that already exists server-side and is transcoding at Bunny — e.g.
 * an mp4 imported by URL, where Bunny fetched the file itself so no bytes ever move
 * from the browser. We skip the upload phase entirely and jump straight to the same
 * encode poller a file upload uses, so its card climbs and lands identically instead
 * of sitting frozen at "Processing 0%".
 */
export function enqueueProcessing(videoId, { title } = {}) {
  if (videoId == null) return;
  if (!items.has(videoId)) {
    items.set(videoId, {
      ...blankItem({ videoId, title }),
      phase: "processing",
    });
    runtime.set(videoId, runtime.get(videoId) || {});
  }
  emitNow();
  // Show the card in the grid straight away, then follow the transcode to completion.
  notifyGrid();
  trackEncoding(videoId);
}

/** Stop sending bytes but keep everything needed to continue. */
export function pause(videoId) {
  const rt = runtime.get(videoId);
  if (!rt?.upload) return;
  rt.aborted = true;
  clearInterval(rt.resignTimer);
  rt.resignTimer = null;
  // shouldTerminate=false: keep the session alive on Bunny so resume works.
  rt.upload.abort(false);
  patch(videoId, { phase: "paused", bytesPerSec: 0, etaSeconds: null });
}

/**
 * Continue an upload that is paused or interrupted.
 * `file` is only required when the manager no longer holds the handle, i.e. after a
 * page reload. The file is checked against the recorded name+size so bytes can't be
 * appended from the wrong source.
 * @returns {Promise<void>}
 */
export async function resume(videoId, file = null) {
  const item = items.get(videoId);
  if (!item) return;
  const rt = runtime.get(videoId) || {};

  const useFile = file || rt.file;
  if (!useFile) {
    patch(videoId, { needsFile: true, error: "Select the same file again to resume this upload." });
    return;
  }
  if (useFile.name !== item.fileName || useFile.size !== item.fileSize) {
    patch(videoId, {
      needsFile: true,
      error: `That's a different file. Expected "${item.fileName}" (${formatBytes(item.fileSize)}).`,
    });
    return;
  }

  patch(videoId, { phase: "uploading", error: null, needsFile: false });

  // Always take a fresh signature: the stored one is usually the reason we stopped.
  let signed = null;
  try {
    const res = await UploadResign(videoId, { contentType: useFile.type });
    signed = res?.data?.data;
  } catch (err) {
    patch(videoId, {
      phase: "interrupted",
      error: "Could not refresh the upload session. Check your connection and retry.",
    });
    return;
  }
  if (!signed?.headers) {
    patch(videoId, { phase: "interrupted", error: "Could not refresh the upload session." });
    return;
  }

  runtime.set(videoId, {
    ...rt,
    file: useFile,
    endpoint: signed.endpoint,
    authHeaders: buildAuthHeaders(signed.headers),
    expiresAtMs: signed.expiresAt ? Number(signed.expiresAt) * 1000 : null,
    contentType: useFile.type || "video/mp4",
    // Prefer the server's record: it survives reloads, unlike anything in memory.
    uploadUrl: signed.tusUploadUrl || rt.uploadUrl || null,
    lastBytes: Number(signed.bytesUploaded) || rt.lastBytes || 0,
    rate: 0,
    lastSampleAt: null,
    lastSampleBytes: 0,
  });

  startTus(videoId, useFile, {
    endpoint: signed.endpoint,
    authHeaders: buildAuthHeaders(signed.headers),
    uploadUrl: signed.tusUploadUrl || rt.uploadUrl || null,
  });
}

/** Abandon an upload: stop sending, delete the partial video, drop the card. */
export async function cancel(videoId) {
  const rt = runtime.get(videoId);
  if (rt) {
    rt.aborted = true;
    clearInterval(rt.resignTimer);
    clearTimeout(rt.encodeTimer);
    try {
      // shouldTerminate=true: tell Bunny to drop the session as well.
      await rt.upload?.abort(true);
    } catch {
      /* aborting a not-yet-started upload is fine */
    }
  }
  try {
    await UploadCancel(videoId);
  } catch (err) {
    console.warn(`Cancel request failed for video ${videoId}`, err);
  }
  runtime.delete(videoId);
  items.delete(videoId);
  emitNow();
  notifyGrid();
}

/** Remove a finished/failed card from the list without touching the video. */
export function dismiss(videoId) {
  const rt = runtime.get(videoId);
  if (rt) {
    clearInterval(rt.resignTimer);
    clearTimeout(rt.encodeTimer);
    runtime.delete(videoId);
  }
  items.delete(videoId);
  emitNow();
}

/** Clear every card that has reached a terminal state. In-flight uploads are kept. */
export function dismissFinished() {
  let changed = false;
  Array.from(items.values()).forEach((it) => {
    if (it.phase === "ready" || it.phase === "failed") {
      const rt = runtime.get(it.videoId);
      if (rt) {
        clearTimeout(rt.encodeTimer);
        runtime.delete(it.videoId);
      }
      items.delete(it.videoId);
      changed = true;
    }
  });
  if (changed) emitNow();
}

// ---------------------------------------------------------------------------
// Encode tracking
// ---------------------------------------------------------------------------

/**
 * Follow Bunny's transcode for a video until it genuinely finishes.
 *
 * The previous implementation stopped after 48 polls (~12 min) or 10 unchanged polls
 * (~2.5 min) and then *removed the card and refreshed the grid* — indistinguishable
 * from success. Here, a slow or queued transcode keeps its card and says so, and only
 * a real terminal state ends the poll.
 */
export function trackEncoding(videoId) {
  const item = items.get(videoId);
  if (!item) return;

  const rt = runtime.get(videoId) || {};
  clearTimeout(rt.encodeTimer);
  rt.encodePolls = 0;
  rt.encodeStartedAt = rt.encodeStartedAt || Date.now();
  runtime.set(videoId, rt);

  patch(videoId, { phase: "processing", bytesPerSec: 0, etaSeconds: null });

  const poll = async () => {
    const cur = runtime.get(videoId);
    if (!cur || !items.has(videoId)) return;
    cur.encodePolls = (cur.encodePolls || 0) + 1;

    try {
      // Always refresh. A non-refreshing poll just re-reads a DB value that only changes
      // on a refreshing poll, so it cannot advance the bar — it only made the UI stale.
      const res = await getProgressStatus(videoId, true);
      const prog = res?.data?.data?.progress;

      if (prog) {
        const encodeProgress = Math.min(
          100,
          Math.max(Number(prog.encode_progress) || 0, Number(prog.overall_progress) || 0)
        );

        // Bunny gave up. Say so plainly instead of leaving a bar frozen at 61%.
        if (prog.is_failed || prog.upload_state === "failed") {
          patch(videoId, {
            phase: "failed",
            encodeProgress,
            error: prog.upload_error || "Processing failed at Bunny. Please re-upload this file.",
          });
          notifyGrid();
          return;
        }

        patch(videoId, { phase: "processing", encodeProgress });

        // Require Bunny's "finished" AND a real 100%. Accepting either one alone let the
        // card say "Ready to use" while Bunny was still encoding at 98%. The server applies
        // the same rule (upload_status='ready'), so the two agree.
        const bunnySaysDone =
          prog.upload_state === "ready" || prog.status === "completed" || prog.is_complete;
        if (bunnySaysDone && encodeProgress >= 100) {
          patch(videoId, { phase: "ready", encodeProgress: 100, error: null });
          notifyGrid();
          // Leave the card up briefly so the user sees it land, then clear it.
          cur.encodeTimer = setTimeout(() => dismiss(videoId), 4000);
          return;
        }
      }
    } catch (err) {
      // Transient API/network failure: keep polling, don't declare anything.
      console.warn(`Progress poll failed for video ${videoId}`, err);
    }

    const elapsed = Date.now() - (cur.encodeStartedAt || Date.now());
    if (elapsed > ENCODE_POLL_CEILING_MS) {
      // Stop the timer but keep the card and be honest about what we know.
      patch(videoId, {
        phase: "processing",
        error: "Still processing at Bunny after 6 hours. Reload to check again.",
      });
      notifyGrid();
      return;
    }

    // Responsive while the transcode is young, cheap once it's clearly a long job.
    cur.encodeTimer = setTimeout(poll, encodePollDelay(elapsed));
  };

  rt.encodeTimer = setTimeout(poll, ENCODE_FIRST_POLL_MS);
}

// ---------------------------------------------------------------------------
// Formatting helpers (shared by the popup and the media grid)
// ---------------------------------------------------------------------------

export function formatBytes(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let value = n / 1024;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value >= 10 || i === 0 ? Math.round(value) : value.toFixed(1)} ${units[i]}`;
}

export function formatDuration(seconds) {
  const s = Number(seconds);
  if (!Number.isFinite(s) || s < 0) return null;
  if (s < 60) return `${Math.round(s)}s`;
  const mins = Math.round(s / 60);
  if (mins < 60) return `${mins} min`;
  const hours = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem ? `${hours}h ${rem}m` : `${hours}h`;
}

/** The one-line status a card shows under its title. */
export function describeItem(item) {
  if (!item) return "";
  switch (item.phase) {
    case "uploading": {
      const parts = [`${formatBytes(item.bytesUploaded)} of ${formatBytes(item.fileSize)}`];
      if (item.bytesPerSec > 0) parts.push(`${formatBytes(item.bytesPerSec)}/s`);
      const eta = formatDuration(item.etaSeconds);
      if (eta) parts.push(`${eta} left`);
      return parts.join(" · ");
    }
    case "paused":
      return `Paused at ${formatBytes(item.bytesUploaded)} of ${formatBytes(item.fileSize)}`;
    case "interrupted":
      return item.error || "Interrupted — can be resumed";
    case "processing":
      return item.encodeProgress > 0
        ? `Transcoding ${item.encodeProgress}%`
        : "Queued for transcoding at Bunny";
    case "ready":
      return "Ready to use";
    case "failed":
      return item.error || "Failed";
    default:
      return "";
  }
}

/** Percentage the progress bar should show for the item's current phase. */
export function barPercent(item) {
  if (!item) return 0;
  if (item.phase === "processing") return item.encodeProgress || 0;
  if (item.phase === "ready") return 100;
  return item.progress || 0;
}
